import { Dispatch, SetStateAction, createContext, useState } from 'react';
import { useProduct } from '../ProductCard';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { EMAIL_REGEX } from '@monofront/business/constants';
import Close from '../../../visuals/Icons/Old/Close';
import Notification from '../../../visuals/Icons/Notification';

export const NotifyMeContext = createContext<{
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}>({
  isOpen: false,
  setIsOpen: () => undefined,
});

export const ProductComeBackSoon: React.FC<{
  userEmail?: string | null;
  handleNotifyMe: (
    email: string,
    handleSuccess?: () => void,
    handleError?: (errorMessageKey: string) => void,
  ) => void;
  isNotifyMeLoading?: boolean;
}> = ({ userEmail, handleNotifyMe, isNotifyMeLoading }) => {
  const product = useProduct();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const tCatalog = useTranslations('Catalog');
  const tAccount = useTranslations('Account');
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<{
    email: string;
    form: void;
  }>();

  if (!product || product.inventory_available) {
    return null;
  }

  return (
    <>
      <div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-top-0 tw-hidden tw-bg-white tw-opacity-50 group-[.out-of-stock]:tw-block" />
      <div
        data-testid="product-come-back-soon-message"
        className={`
        tw-absolute tw-left-0 tw-right-0 tw-top-[65px] tw-hidden tw-w-full
        tw-justify-center group-[.out-of-stock]:tw-flex
        desktop:tw-top-[77px]
        `}
      >
        <p className="tw-lf-subtitle-sm tw-bg-white tw-text-center">
          {tCatalog('comesBackSoon')}
        </p>
      </div>
      <div
        className={`
      tw-hidden tw-h-[36px] tw-grid-area-[notify]
      group-[.out-of-stock]:tw-flex
      `}
      >
        {/* Placeholder */}
      </div>
      <div
        className={`
          tw-absolute tw-bottom-0 tw-left-0
          tw-right-0 tw-mx-2 !tw-mb-2 tw-hidden group-[.out-of-stock]:tw-flex desktop:tw-mx-4 desktop:!tw-mb-4
          `}
      >
        <button
          onClick={(e) => {
            userEmail ? handleNotifyMe(userEmail) : setIsOpen((prev) => !prev);
            e.preventDefault();
            e.stopPropagation();
          }}
          data-testid="product-notify-me-button"
          className="tw-lf-button tw-lf-button-secondary tw-lf-button-sm tw-w-full"
        >
          <Notification />
          <span>{tCatalog('notifyMeV2')}</span>
        </button>
      </div>
      {isOpen && (
        <div
          className="tw-lf-modal-overlay tw-lf-modal-overlay-centered"
          onClick={(e) => {
            setIsOpen(false);
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div
            className="tw-align-center tw-lf-modal-content tw-lf-modal-content-max-width tw-flex tw-flex-col tw-justify-center tw-gap-4 tw-pt-5"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <button
              onClick={() => setIsOpen(false)}
              className="tw-lf-modal-close-button"
            >
              <Close width={10} height={10} />
            </button>
            <h3 className="tw-lf-body-regular-lg">{product.title}</h3>
            <p>{tCatalog('notifyMeDetails')}</p>
            <form
              className="tw-align-center tw-flex tw-flex-col tw-justify-center tw-gap-4"
              onSubmit={handleSubmit(({ email }: Record<string, any>) =>
                handleNotifyMe(
                  email,
                  () => setIsOpen(false),
                  (errorMessageKey) =>
                    setError('form', {
                      type: 'serverError',
                      message: tCatalog(errorMessageKey as 'notifyMeError'),
                    }),
                ),
              )}
            >
              {errors?.email?.message && (
                <div className="tw-lf-body-regular-md tw-text-center tw-font-bold tw-text-red-600">
                  {errors?.email?.message}
                </div>
              )}
              {errors?.form?.message && (
                <div className="tw-lf-body-regular-md tw-text-center tw-font-bold tw-text-red-600">
                  {errors?.form?.message}
                </div>
              )}
              <input
                className="tw-lf-body-regular-md tw-box-border tw-h-11 tw-w-full tw-appearance-none tw-rounded-lg tw-border tw-border-solid tw-border-blue-400 tw-bg-white tw-px-4 tw-py-2 tw-outline-none"
                type="email"
                placeholder="Email"
                {...register('email', {
                  required: true,
                  pattern: {
                    value: EMAIL_REGEX,
                    message: tAccount('useValidEmail'),
                  },
                })}
                data-testid="ModalNotifyMe-Input-1"
              />
              <button
                className="tw-lf-button tw-lf-button-primary tw-lf-button-md tw-lf-button-min-width tw-w-fit tw-self-center"
                type="submit"
                disabled={isNotifyMeLoading}
                data-testid="ModalNotifyMe-Button-submit"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {tCatalog('notifyMe')}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export const HorizontalProductComeBackSoon: React.FC<{
  userEmail?: string | null;
  handleNotifyMe: (
    email: string,
    handleSuccess?: () => void,
    handleError?: (errorMessageKey: string) => void,
  ) => void;
  isNotifyMeLoading?: boolean;
}> = ({ userEmail, handleNotifyMe, isNotifyMeLoading }) => {
  const product = useProduct();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const tCatalog = useTranslations('Catalog');
  const tAccount = useTranslations('Account');
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<{
    email: string;
    form: void;
  }>();

  if (!product || product.inventory_available) {
    return null;
  }

  return (
    <>
      <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-hidden tw-h-full tw-bg-white tw-opacity-50 group-[.out-of-stock]:tw-block" />
      <div
        data-testid="product-come-back-soon-message"
        className={`
        tw-absolute tw-left-1/2 tw-top-1/2 !tw-my-0 tw-hidden -tw-translate-x-1/2 -tw-translate-y-1/2 tw-items-center
        tw-justify-center tw-space-y-1 group-[.out-of-stock]:tw-flex group-[.out-of-stock]:tw-flex-col
        `}
      >
        <p className="tw-lf-subtitle-sm tw-bg-white tw-text-center">
          {tCatalog('comesBackSoon')}
        </p>
        <div
          className={`
      tw-hidden tw-grid-area-[notify]
      group-[.out-of-stock]:tw-flex
      `}
        >
          {/* Placeholder */}
        </div>
        <div
          className={`
          tw-hidden tw-w-[198px] group-[.out-of-stock]:tw-flex
          `}
        >
          <button
            onClick={(e) => {
              userEmail
                ? handleNotifyMe(userEmail)
                : setIsOpen((prev) => !prev);
              e.preventDefault();
              e.stopPropagation();
            }}
            data-testid="product-notify-me-button"
            className="tw-lf-button tw-lf-button-secondary tw-lf-button-sm tw-w-full"
          >
            <Notification />
            <span>{tCatalog('notifyMeV2')}</span>
          </button>
        </div>
      </div>

      {isOpen && (
        <div
          className="tw-lf-modal-overlay tw-lf-modal-overlay-centered"
          onClick={(e) => {
            setIsOpen(false);
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div
            className="tw-align-center tw-lf-modal-content tw-lf-modal-content-max-width tw-flex tw-flex-col tw-justify-center tw-gap-4 tw-pt-5"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <button
              onClick={() => setIsOpen(false)}
              className="tw-lf-modal-close-button"
            >
              <Close width={10} height={10} />
            </button>
            <h3 className="tw-lf-body-regular-lg">{product.title}</h3>
            <p>{tCatalog('notifyMeDetails')}</p>
            <form
              className="tw-align-center tw-flex tw-flex-col tw-justify-center tw-gap-4"
              onSubmit={handleSubmit(({ email }: Record<string, any>) =>
                handleNotifyMe(
                  email,
                  () => setIsOpen(false),
                  (errorMessageKey) =>
                    setError('form', {
                      type: 'serverError',
                      message: tCatalog(errorMessageKey as 'notifyMeError'),
                    }),
                ),
              )}
            >
              {errors?.email?.message && (
                <div className="tw-lf-body-regular-md tw-text-center tw-font-bold tw-text-red-600">
                  {errors?.email?.message}
                </div>
              )}
              {errors?.form?.message && (
                <div className="tw-lf-body-regular-md tw-text-center tw-font-bold tw-text-red-600">
                  {errors?.form?.message}
                </div>
              )}
              <input
                className="tw-lf-body-regular-md tw-box-border tw-h-11 tw-w-full tw-appearance-none tw-rounded-lg tw-border tw-border-solid tw-border-blue-400 tw-bg-white tw-px-4 tw-py-2 tw-outline-none"
                type="email"
                placeholder="Email"
                {...register('email', {
                  required: true,
                  pattern: {
                    value: EMAIL_REGEX,
                    message: tAccount('useValidEmail'),
                  },
                })}
                data-testid="ModalNotifyMe-Input-1"
              />
              <button
                className="tw-lf-button tw-lf-button-primary tw-lf-button-md tw-lf-button-min-width tw-w-fit tw-self-center"
                type="submit"
                disabled={isNotifyMeLoading}
                data-testid="ModalNotifyMe-Button-submit"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {tCatalog('notifyMe')}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
